import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAppSelector } from '../../redux/hooks';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { Eye, EyeOff, ChevronDown, ChevronUp, User, Shield } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import { updateProfileFields } from '../../redux/slices/authSlice';
import { useUpdateAccountMutation } from '../../redux/api/authApi';

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  role: z.string().optional(),
  phoneNumber: z.string().optional(),
});

const passwordSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .refine((val) => /[A-Z]/.test(val), 'Password must contain at least one uppercase letter')
    .refine((val) => /\d/.test(val), 'Password must contain at least one number')
    .refine((val) => /[^A-Za-z0-9]/.test(val), 'Password must contain at least one special character'),
  confirmPassword: z
    .string()
    .min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type ProfileFormData = z.infer<typeof profileSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

const UserProfileTab: React.FC = () => {
  const profile = useAppSelector((s) => s.auth.profile);
  const dispatch = useDispatch<AppDispatch>();

  const [email] = useState<string>(profile.email ?? '');
  const [isProfileOpen, setIsProfileOpen] = useState<boolean>(true);
  const [isSecurityOpen, setIsSecurityOpen] = useState<boolean>(false);
  const [isLoadingProfile] = useState<boolean>(false);
  const [updateAccount, { isLoading: isUpdatingAccount }] = useUpdateAccountMutation();

  // Security state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Profile form
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    watch: watchProfile,
    formState: { errors: profileErrors, isDirty: isProfileDirty },
    reset: resetProfile,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      role: profile.role ?? '',
      phoneNumber: profile.phone ?? '',
    },
  });

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    watch: watchPassword,
    formState: { errors: passwordErrors, isSubmitting: isUpdatingPassword },
    reset: resetPassword,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = watchPassword('password');
  const confirmPassword = watchPassword('confirmPassword');

  const rules = useMemo(() => ({
    length: password ? password.length >= 8 : false,
    uppercase: password ? /[A-Z]/.test(password) : false,
    number: password ? /\d/.test(password) : false,
    special: password ? /[^A-Za-z0-9]/.test(password) : false,
    match: confirmPassword ? password === confirmPassword : false,
  }), [password, confirmPassword]);

  // Reset profile form when profile data changes
  useEffect(() => {
    resetProfile({
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      role: profile.role ?? '',
      phoneNumber: profile.phone ?? '',
    });
  }, [profile.firstName, profile.lastName, profile.role, profile.phone, resetProfile]);

  const handleSaveProfile = async (data: ProfileFormData) => {
    const trimmedFirstName = data.firstName.trim();
    const trimmedLastName = data.lastName.trim();
    const trimmedRole = data.role?.trim() || '';
    const trimmedPhoneNumber = data.phoneNumber?.trim() || '';

    try {
      const payload = {
        first_name: trimmedFirstName,
        last_name: trimmedLastName,
        role: trimmedRole || undefined,
        phone: trimmedPhoneNumber || undefined,
      };

      await updateAccount(payload).unwrap();
      toast.success('Profile updated successfully');
      dispatch(
        updateProfileFields({
          firstName: trimmedFirstName || null,
          lastName: trimmedLastName || null,
          phone: trimmedPhoneNumber || null,
          role: trimmedRole || null,
        })
      );
      resetProfile(data, { keepValues: true });
    } catch (error: any) {
      console.warn('Profile update failed:', error);
      toast.error(error?.data?.message || 'Failed to update profile');
    }
  };

  const handleChangePassword = async (data: PasswordFormData) => {
    const { error } = await supabase.auth.updateUser({ password: data.password });
    if (error) {
      toast.error(error.message || 'Failed to update password');
      return;
    }
    toast.success('Password updated');
    resetPassword();
  };

  return (
    <div className="eg-page-shell flex flex-col">
      {/* Header */}
      <div className="eg-page-header relative">
        <div className="eg-page-header-glow" />
        <div className="px-6 pt-5 pb-4">
          <h1 className="text-2xl font-bold eg-text-primary mb-1">Account Settings</h1>
          <p className="text-sm eg-text-secondary">Manage your profile and security preferences</p>
        </div>
        <div className="eg-page-accent-divider" />
      </div>

      <div className="eg-page-content flex-1 overflow-auto">
      <div className="px-6 py-5 max-w-2xl mx-auto">
        {/* Profile section */}
        <div className="eg-card-elevated mb-4 overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 eg-text-primary hover:bg-[var(--color-surface-hover)] transition-colors"
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            aria-expanded={isProfileOpen}
          >
            <span className="flex items-center gap-2 text-base font-semibold">
              <User className="w-4 h-4" />
              Personal Information
            </span>
            {isProfileOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {isProfileOpen && (
            <div className="p-6 border-t border-[var(--color-border)]">
              <form onSubmit={handleProfileSubmit(handleSaveProfile)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm eg-text-secondary mb-1">Email</label>
                    <input
                      type="email"
                      value={email}
                      disabled
                      className="eg-input w-full disabled:opacity-70"
                    />
                  </div>
                  <div>
                    <label className="block text-sm eg-text-secondary mb-1">First Name</label>
                    <input
                      type="text"
                      {...registerProfile('firstName')}
                      className={`eg-input w-full ${
                        profileErrors.firstName ? 'border-red-500' : ''
                      }`}
                      placeholder="Enter your first name"
                      disabled={isLoadingProfile}
                    />
                    {profileErrors.firstName && (
                      <p className="mt-1 text-sm text-red-400">{profileErrors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm eg-text-secondary mb-1">Last Name</label>
                    <input
                      type="text"
                      {...registerProfile('lastName')}
                      className={`eg-input w-full ${
                        profileErrors.lastName ? 'border-red-500' : ''
                      }`}
                      placeholder="Enter your last name"
                      disabled={isLoadingProfile}
                    />
                    {profileErrors.lastName && (
                      <p className="mt-1 text-sm text-red-400">{profileErrors.lastName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm eg-text-secondary mb-1">Role</label>
                    <input
                      type="text"
                      {...registerProfile('role')}
                      className="eg-input w-full"
                      placeholder="e.g., admin, operator"
                      disabled={isLoadingProfile}
                    />
                    {profileErrors.role && (
                      <p className="mt-1 text-sm text-red-400">{profileErrors.role.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm eg-text-secondary mb-1">Phone Number</label>
                    <input
                      type="tel"
                      {...registerProfile('phoneNumber')}
                      className="eg-input w-full"
                      placeholder="Enter your phone number"
                      disabled={isLoadingProfile}
                    />
                    {profileErrors.phoneNumber && (
                      <p className="mt-1 text-sm text-red-400">{profileErrors.phoneNumber.message}</p>
                    )}
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-3">
                  <button
                    type="submit"
                    className="eg-btn-secondary"
                    disabled={isLoadingProfile || !isProfileDirty || isUpdatingAccount}
                  >
                    {isUpdatingAccount ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Security section */}
        <div className="eg-card-elevated overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 eg-text-primary hover:bg-[var(--color-surface-hover)] transition-colors"
            onClick={() => setIsSecurityOpen(!isSecurityOpen)}
            aria-expanded={isSecurityOpen}
          >
            <span className="flex items-center gap-2 text-base font-semibold">
              <Shield className="w-4 h-4" />
              Privacy and Security
            </span>
            {isSecurityOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {isSecurityOpen && (
            <div className="p-6 border-t border-[var(--color-border)]">
              <form onSubmit={handlePasswordSubmit(handleChangePassword)} className="space-y-4">
                <div className="eg-text-primary">Change Password</div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="New password"
                    {...registerPassword('password')}
                    className={`eg-input w-full pr-10 ${
                      passwordErrors.password ? 'border-red-500' : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 eg-text-muted hover:eg-text-primary transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                  {passwordErrors.password && (
                    <p className="mt-1 text-sm text-red-400">{passwordErrors.password.message}</p>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    {...registerPassword('confirmPassword')}
                    className={`eg-input w-full pr-10 ${
                      passwordErrors.confirmPassword ? 'border-red-500' : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 eg-text-muted hover:eg-text-primary transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                  {passwordErrors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-400">{passwordErrors.confirmPassword.message}</p>
                  )}
                </div>
                <ul className="text-xs eg-text-secondary space-y-1">
                  <li className={rules.length ? 'text-green-400' : ''}>At least 8 characters</li>
                  <li className={rules.uppercase ? 'text-green-400' : ''}>At least one uppercase letter</li>
                  <li className={rules.number ? 'text-green-400' : ''}>At least one number</li>
                  <li className={rules.special ? 'text-green-400' : ''}>At least one special character</li>
                  <li className={rules.match ? 'text-green-400' : ''}>Passwords match</li>
                </ul>
                <button
                  type="submit"
                  disabled={isUpdatingPassword}
                  className="eg-btn-primary w-full"
                >
                  {isUpdatingPassword ? 'Updating…' : 'Update password'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default UserProfileTab;
